// Playing one asset, on the `visitor` surface: what to fetch, what was watched, the source file.

import { VISITOR_BASE, visitor, withQuery } from '../client'
import type { components } from '../visitor.schema'

export type PlaybackInfoView = components['schemas']['PlaybackInfoView']
export type FetchPlaybackInfoRequest = components['schemas']['FetchPlaybackInfoRequest']
export type BatchFetchPlaybackInfoRequest = components['schemas']['BatchFetchPlaybackInfoRequest']
export type BatchPlaybackInfoResponse = components['schemas']['BatchPlaybackInfoResponse']
export type ReportHeartbeatRequest = components['schemas']['ReportHeartbeatRequest']
export type WatchedRange = components['schemas']['WatchedRange']
export type SignedUrl = components['schemas']['SignedUrl']
export type VideoChapter = components['schemas']['VideoChapter']
export type LinkedFile = components['schemas']['LinkedFile']
export type SubtitleTrack = components['schemas']['SubtitleTrack']
export type ImageVariant = components['schemas']['ImageVariant']

const HEARTBEAT = '/playback/heartbeat/'

/**
 * What to fetch for one asset — a manifest, a source, an image's sizes — and where this device got
 * to. The `playback_token` is the credential: 401 once it no longer verifies, 404 for an asset it
 * does not reach. `blocked: true` is the owner's empty wallet, on a public token only, and it is
 * not an error.
 */
export function fetchPlaybackInfo(request: FetchPlaybackInfoRequest): Promise<PlaybackInfoView> {
  return visitor.post<PlaybackInfoView>('/playback/info/', request)
}

/** The same for up to twenty assets. One the token does not reach is absent from the map. */
export function fetchPlaybackInfoBatch(request: BatchFetchPlaybackInfoRequest): Promise<BatchPlaybackInfoResponse> {
  return visitor.post<BatchPlaybackInfoResponse>('/playback/info/batch/', request)
}

/** Progress while something plays. 204 whether it was recorded or deliberately throttled. */
export function reportHeartbeat(request: ReportHeartbeatRequest): Promise<void> {
  return visitor.post<void>(HEARTBEAT, request)
}

/** The heartbeat's address, for `navigator.sendBeacon`, which takes a URL rather than a client. */
export const HEARTBEAT_URL = `${VISITOR_BASE}${HEARTBEAT}`

/**
 * Where a publication that offers its source file hands it over: a link for an `<a download>`,
 * never a `fetch`, since it answers a 302 to the file. 403 when the publication does not offer
 * it, which a token minted in the application never does.
 */
export function playbackDownloadUrl(assetId: string, playbackToken: string): string {
  return withQuery(`${VISITOR_BASE}/playback/download/`, {
    asset_id: assetId,
    playback_token: playbackToken,
  })
}
